/**
 * Grade, wind and rough-road data for a route's navigation warnings, in the
 * shape ZephyrNavigationActivity reads from the overlays (gradeSegments,
 * windBadges, roadQualitySegments). The page builds these at navigation
 * start (main.js); the route-hazards Lambda builds them for trips started
 * from Android Auto, where there's no page (lambda/route-hazards).
 */
import { ElevationService } from './ElevationService.js';
import { WindService } from './WindService.js';
import { RoadQualityService } from './RoadQualityService.js';
import { prepareWindWaypoints, fetchWindHotspots } from './windWaypoints.js';

/**
 * One warning point per steep stretch (ElevationService.findGradeStretches),
 * resolved against the elevation points to real lat/lng -- a stretch only
 * carries an index, not a location.
 */
export function gradeSegmentsFrom(elevationAnalysis) {
    const points = elevationAnalysis?.points || [];
    return (elevationAnalysis?.stats?.gradeStretches || [])
        .map(g => {
            const p = points[g.location];
            return p ? { lat: p.lat, lng: p.lng, gradePct: g.grade, isDescending: !g.isClimb } : null;
        })
        .filter(Boolean);
}

/**
 * Only the poor-rated segments -- same filtering the map card applies, not
 * the full segment list (free/fair/good segments have nothing worth speaking).
 */
export function roadQualitySegmentsFrom(roadQuality) {
    return (roadQuality?.segments || [])
        .filter(s => s.iriRating === 'poor' && s.startLat && s.startLng)
        .map(s => ({ lat: s.startLat, lng: s.startLng, highway: s.highway || 'this road' }));
}

/** Same sample count the page uses for route elevation (UIController). */
export function elevationSampleCount(coordinates) {
    return Math.min(300, Math.max(200, Math.floor(coordinates.length / 5)));
}

/**
 * All three for one route. Each part is fetched on its own; a part that
 * fails comes back empty and is named in `failed`, so the others still warn.
 * @param {Object} options
 * @param {Array} options.coordinates - Route [{ lat, lng }]
 * @param {Array} [options.highways] - Route numbers ("I-70"), from rv-route
 * @param {Date|string} [options.departureTime] - For the wind forecast
 * @param {Object} [options.rvSpecs] - Rig, for the wind thresholds
 * @param {Object} [options.travelPreferences] - Cruising speed for arrival times
 * @param {string} [options.userPlan] - Sent with the road-quality request
 * @param {string} options.backendUrl - API base for the wind hotspots
 */
export async function buildRouteHazards({ coordinates, highways = [], departureTime = new Date(), rvSpecs = {}, travelPreferences = {}, userPlan = 'free', backendUrl }) {
    const failed = [];
    const part = (name, work) => work().catch(err => {
        console.warn(`[routeHazards] ${name} failed:`, err.message);
        failed.push(name);
        return [];
    });

    const [gradeSegments, windBadges, roadQualitySegments] = await Promise.all([
        part('grade', async () => {
            // Not getElevationAlongPath(): on failure that falls back to
            // made-up demo elevations, which would warn about grades that
            // aren't there.
            const analysis = await new ElevationService().getRealElevationData(coordinates, elevationSampleCount(coordinates));
            return gradeSegmentsFrom(analysis);
        }),
        part('wind', async () => {
            const hotspots = await fetchWindHotspots(coordinates, backendUrl);
            const waypoints = prepareWindWaypoints(coordinates, { hotspots });
            const profile = await new WindService().buildRouteWindProfile(departureTime, waypoints, rvSpecs, travelPreferences);
            return profile.windProfile;
        }),
        part('roadQuality', async () => {
            const roadQuality = await new RoadQualityService().getRoadQualityForRoute(coordinates, userPlan, highways);
            return roadQualitySegmentsFrom(roadQuality);
        })
    ]);

    return { gradeSegments, windBadges, roadQualitySegments, failed };
}
